import { eq } from "drizzle-orm";
import {
  meal_plan_entry,
  type PowerSyncDrizzleDB,
  recipe,
  recipe_ingredient,
} from "@mealplan/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { parseStringArray, serializeStringArray } from "./queries";

export interface RecipeIngredientInput {
  /** id existente (edición) o undefined (nuevo). */
  id?: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  aisleId?: string | null;
}

/** Nutrición POR PORCIÓN de la receta (00052). Todos opcionales. */
export interface RecipeNutritionInput {
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
}

export interface RecipeInput {
  name: string;
  servings: number;
  steps: string[];
  tags: string[];
  notes?: string | null;
  photoUrl?: string | null;
  ingredients: RecipeIngredientInput[];
  /**
   * Nutrición por porción. `undefined` = NO tocar la existente (p. ej.
   * restaurar una versión L4 conserva la nutrición actual — no versionada);
   * un objeto (o campos null) = escribir exactamente esos valores.
   */
  nutrition?: RecipeNutritionInput;
}

const DEFAULT_SERVINGS = 2;

function sanitizeServings(servings: number): number {
  if (!Number.isFinite(servings) || servings <= 0) {
    return DEFAULT_SERVINGS;
  }
  return Math.round(servings);
}

/** Crea la receta con sus ingredientes en una transacción local. */
export async function createRecipeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: RecipeInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx.insert(recipe).values({
      id,
      user_id: userId,
      name: input.name.trim(),
      photo_url: input.photoUrl ?? null,
      servings: sanitizeServings(input.servings),
      steps: serializeStringArray(input.steps),
      tags: serializeStringArray(input.tags),
      notes: input.notes?.trim() || null,
      calories: input.nutrition?.calories ?? null,
      protein_g: input.nutrition?.proteinG ?? null,
      carbs_g: input.nutrition?.carbsG ?? null,
      fat_g: input.nutrition?.fatG ?? null,
      created_at: now,
      updated_at: now,
    });

    let order = 0;
    for (const ingredientInput of input.ingredients) {
      if (!ingredientInput.name.trim()) {
        continue;
      }
      await trx.insert(recipe_ingredient).values({
        id: generateUUID(),
        recipe_id: id,
        user_id: userId,
        name: ingredientInput.name.trim(),
        quantity: ingredientInput.quantity,
        unit: ingredientInput.unit?.trim() || null,
        aisle_id: ingredientInput.aisleId ?? null,
        sort_order: order,
        created_at: now,
        updated_at: now,
      });
      order += 1;
    }
  });

  return id;
}

/**
 * Actualiza la receta y REEMPLAZA su set de ingredientes (estrategia simple y
 * robusta para el editor de F1: borra los que ya no están, upserta el resto).
 */
export async function updateRecipeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  recipeId: string,
  input: RecipeInput,
): Promise<void> {
  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx
      .update(recipe)
      .set({
        name: input.name.trim(),
        photo_url: input.photoUrl ?? null,
        servings: sanitizeServings(input.servings),
        steps: serializeStringArray(input.steps),
        tags: serializeStringArray(input.tags),
        notes: input.notes?.trim() || null,
        // `nutrition === undefined` NO toca la nutrición existente (restore L4).
        ...(input.nutrition !== undefined && {
          calories: input.nutrition.calories,
          protein_g: input.nutrition.proteinG,
          carbs_g: input.nutrition.carbsG,
          fat_g: input.nutrition.fatG,
        }),
        updated_at: now,
      })
      .where(eq(recipe.id, recipeId));

    const existing = await trx
      .select({ id: recipe_ingredient.id })
      .from(recipe_ingredient)
      .where(eq(recipe_ingredient.recipe_id, recipeId));
    const keptIds = new Set(
      input.ingredients.map((item) => item.id).filter((id): id is string => Boolean(id)),
    );

    for (const row of existing) {
      if (!keptIds.has(row.id)) {
        await trx.delete(recipe_ingredient).where(eq(recipe_ingredient.id, row.id));
      }
    }

    let order = 0;
    for (const ingredientInput of input.ingredients) {
      const name = ingredientInput.name.trim();
      if (!name) {
        continue;
      }
      const values = {
        name,
        quantity: ingredientInput.quantity,
        unit: ingredientInput.unit?.trim() || null,
        aisle_id: ingredientInput.aisleId ?? null,
        sort_order: order,
        updated_at: now,
      };
      if (ingredientInput.id) {
        await trx
          .update(recipe_ingredient)
          .set(values)
          .where(eq(recipe_ingredient.id, ingredientInput.id));
      } else {
        await trx.insert(recipe_ingredient).values({
          id: generateUUID(),
          recipe_id: recipeId,
          user_id: userId,
          created_at: now,
          ...values,
        });
      }
      order += 1;
    }
  });
}

/** Ingrediente tal y como viaja dentro del snapshot `ingredients` de una
 * versión (`[{name, quantity, unit, aisle_id, sort_order}, …]`). */
interface VersionIngredientNode {
  name: string;
  quantity: number | null;
  unit: string | null;
  aisle_id: string | null;
  sort_order: number;
}

/** Estado de una versión (L4) necesario para restaurar: campos del padre +
 * ingredientes serializados (bucket local o API camelCase→snake_case). */
export interface RecipeVersionSnapshot {
  name: string | null;
  servings: number | null;
  steps: string | null;
  tags: string | null;
  notes: string | null;
  photo_url: string | null;
  ingredients: string | null;
}

function isVersionIngredientNode(value: unknown): value is VersionIngredientNode {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const node = value as Partial<Record<keyof VersionIngredientNode, unknown>>;
  return typeof node.name === "string";
}

/** Parsea el snapshot `ingredients` (JSON del bucket local o del API). */
export function parseVersionIngredients(ingredients: string | null): VersionIngredientNode[] {
  if (!ingredients) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(ingredients);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isVersionIngredientNode).map((node) => ({
      name: node.name,
      quantity: typeof node.quantity === "number" ? node.quantity : null,
      unit: typeof node.unit === "string" ? node.unit : null,
      aisle_id: typeof node.aisle_id === "string" ? node.aisle_id : null,
      sort_order: typeof node.sort_order === "number" ? node.sort_order : 0,
    }));
  } catch {
    return [];
  }
}

/**
 * Restaura una versión del historial (L4): escritura NORMAL hacia adelante.
 *
 * Reutiliza `updateRecipeMutation` (repone los campos del padre + REEMPLAZA los
 * ingredientes por los del snapshot) en UNA transacción. Nunca se reescribe la
 * historia: el write-path del server archiva el estado ACTUAL como versión
 * nueva ANTES de aplicar esto (el estado pre-restore se conserva de forma
 * natural, sin colapso). NO toca `deleted_at` (la papelera queda intacta).
 */
export async function restoreRecipeVersionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  recipeId: string,
  version: RecipeVersionSnapshot,
): Promise<void> {
  const ingredients = parseVersionIngredients(version.ingredients)
    .toSorted((a, b) => a.sort_order - b.sort_order)
    .map((node) => ({
      name: node.name,
      quantity: node.quantity,
      unit: node.unit,
      aisleId: node.aisle_id,
    }));
  const input: RecipeInput = {
    name: version.name ?? "",
    servings: version.servings ?? DEFAULT_SERVINGS,
    steps: parseStringArray(version.steps),
    tags: parseStringArray(version.tags),
    notes: version.notes ?? null,
    photoUrl: version.photo_url ?? null,
    ingredients,
  };
  await updateRecipeMutation(db, userId, recipeId, input);
}

/**
 * Papelera L2: mueve la receta a la papelera (soft-delete). NO borra sus
 * ingredientes ni sus entradas del planner: al restaurar, los slots que la
 * referencian vuelven a resolverse. Las vistas vivas la ocultan (deleted_at
 * IS NULL); la generación de la lista de compra tampoco la usa.
 */
export async function trashRecipeMutation(db: PowerSyncDrizzleDB, recipeId: string): Promise<void> {
  await db
    .update(recipe)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(recipe.id, recipeId));
}

/** Restaura una receta de la papelera (deleted_at → NULL). */
export async function restoreRecipeMutation(
  db: PowerSyncDrizzleDB,
  recipeId: string,
): Promise<void> {
  await db
    .update(recipe)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(recipe.id, recipeId));
}

/**
 * Borrado DEFINITIVO ("eliminar para siempre" desde la papelera): borra la
 * receta, sus ingredientes y sus entradas del plan en cascada. Irreversible.
 */
export async function deleteRecipeMutation(
  db: PowerSyncDrizzleDB,
  recipeId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(meal_plan_entry).where(eq(meal_plan_entry.recipe_id, recipeId));
    await trx.delete(recipe_ingredient).where(eq(recipe_ingredient.recipe_id, recipeId));
    await trx.delete(recipe).where(eq(recipe.id, recipeId));
  });
}
